/**
 * Prefetch popular routes during idle time to ensure instant navigation
 */

const routeImports: Record<string, () => Promise<unknown>> = {
  '/blog': () => import('@/pages/Blog'),
  '/faq': () => import('@/pages/FAQ'),
  '/careers': () => import('@/pages/Careers'),
  '/free-marketing': () => import('@/pages/FreeMarketing'),
  '/platform-demos': () => import('@/pages/PlatformDemos'),
  '/agency-owners': () => import('@/pages/AgencyOwners'),
  '/done-for-you': () => import('@/pages/DoneForYou'),
  '/ai': () => import('@/pages/AIPlatform'),
};

// Track already prefetched routes
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a specific route's chunk
 */
export const prefetchRoute = (path: string): void => {
  if (prefetchedRoutes.has(path)) return;
  
  const importer = routeImports[path];
  if (importer) {
    prefetchedRoutes.add(path);
    importer().catch(() => {
      // Silently fail - route will load normally when navigated
      prefetchedRoutes.delete(path);
    });
  }
};

/**
 * Prefetch all popular routes during browser idle time
 * Called once after initial page load
 */
export const prefetchPopularRoutes = (): void => {
  if (typeof window === 'undefined') return;

  const prefetchAll = () => {
    Object.keys(routeImports).forEach((path) => {
      prefetchRoute(path);
    });
  };

  // Use requestIdleCallback for optimal performance
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(prefetchAll, { timeout: 3000 });
  } else {
    // Fallback: prefetch after 2 seconds
    setTimeout(prefetchAll, 2000);
  }
};